// components/FullScreenTranslationViewer.jsx - Full-screen viewer for translation editing
import React, { useState, useRef, useEffect } from 'react';
import TinyMCEEditor from './TinyMCEEditor';
import DocumentViewer from './DocumentViewer';
import ChatbotWidget from './ChatbotWidget';
// import html2pdf from 'html2pdf.js';
import html2canvas from 'html2canvas';
import useApiClient from '../services/api';
import { Rnd } from 'react-rnd';
import { useAuth } from '../context/AuthContext';
import { formatRelativeTime } from '../utils/timeUtils';

const FullScreenTranslationViewer = ({
  originalDocument,
  htmlContent,
  onHtmlChange,
  editorRef,
  documentType = '',
  documentTitle = 'Document',
  onSave,
  onApprove,
  saving = false,
  lastSaved = null,
  selectedPage = 1,
  totalPages = 1,
  onPageChange,
  // Chatbot props
  documentId,
  onHtmlUpdate,
  selectedText = '',
  selectionContext = '',
  onProcessMessage,
  onSelectionChange,
  chatbotConfig = {},
  sourceLanguage = '',
  targetLanguage = '',
  // PDF export props
  allHtmlPages = [], // Array of HTML content for all pages
  projectName = 'Translation',
  // Title editing callback
  onTitleChange
}) => {
  const [viewMode, setViewMode] = useState('split'); // 'split', 'original-full', 'editor-full'
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [splitRatio, setSplitRatio] = useState(50); // Percentage for left panel
  const [isDragging, setIsDragging] = useState(false);
  const [showChatbot, setShowChatbot] = useState(true);
  const [chatbotMode, setChatbotMode] = useState('overlay'); // 'overlay', 'panel', 'hidden'
  const [isExporting, setIsExporting] = useState(false);
  // const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printSettings, setPrintSettings] = useState({
    pageSize: 'a4',
    orientation: 'portrait',
    margin: 10,
    scale: 1,
  });
  // const [previewKey, setPreviewKey] = useState(0); // For force re-render of preview
  const [previewBitmap, setPreviewBitmap] = useState(null); // Chrome-like bitmap preview
  const [previewBitmaps, setPreviewBitmaps] = useState([]); // Array of bitmaps for multi-page
  const [isGeneratingBitmap, setIsGeneratingBitmap] = useState(false);

  // Document title editing state
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState(documentTitle);
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const [titleError, setTitleError] = useState(null);
  const [currentTitle, setCurrentTitle] = useState(documentTitle); // Local state for immediate UI update

  // API client for authenticated requests
  const apiClient = useApiClient();

  // Get user profile from AuthContext
  const { userProfile } = useAuth();

  // State for triggering re-render of relative time display
  const [, setTimeUpdateTrigger] = useState(0);

  // Update relative time display every minute
  useEffect(() => {
    if (!lastSaved) return;

    const interval = setInterval(() => {
      setTimeUpdateTrigger(prev => prev + 1);
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [lastSaved]);

  // Stamp/Signature overlay state
  const [stamps, setStamps] = useState([]); // Array of stamp objects {id, src, x, y, width, height, pageIndex}
  // const [isDraggingFile, setIsDraggingFile] = useState(false);
  const previewContainerRef = useRef(null);
  
  // Local state for content synchronization
  const [currentContent, setCurrentContent] = useState(htmlContent);
  const [originalStyles, setOriginalStyles] = useState(''); // Store original styles once
  
  const containerRef = useRef(null);
  const resizerRef = useRef(null);
  const documentViewerRef = useRef(null);
  // const previewIframeRef = useRef(null);
  const viewModeRef = useRef(viewMode); // Track current view mode

  // Generate Chrome-like bitmap preview with proper styles and scaling
  const generateBitmapPreview = async (settings = printSettings) => {
    setIsGeneratingBitmap(true);
    setPreviewBitmap(null);
    setPreviewBitmaps([]);

    try {
      // Step 1: Get all pages content
      const latestContent = captureCurrentContent();

      // Prepare pages array - either from allHtmlPages or single current content
      const pagesToRender = allHtmlPages && allHtmlPages.length > 0
        ? allHtmlPages.map((page, index) => {
            return index === selectedPage - 1 ? latestContent : (page.content || page);
          })
        : [latestContent || currentContent || htmlContent || '<p>Preview content</p>'];

      console.log(`Generating bitmap preview for ${pagesToRender.length} page(s)`);

      // Step 2: Process each page separately
      const bitmaps = [];

      for (let pageIndex = 0; pageIndex < pagesToRender.length; pageIndex++) {
        const pageContent = pagesToRender[pageIndex];

        // Extract styles from the content (like TinyMCE styles)
        const extractedStyles = extractStylesFromHTML(pageContent) || '';

        // Apply scaling to BOTH content AND canvas size
        const baseWidth = 794; // A4 width in pixels at 96 DPI
        const baseHeight = 1123; // A4 height in pixels at 96 DPI
        const baseFontSize = 12;

        // Scale affects both canvas size AND font sizes/element spacing
        const scaleMultiplier = settings.scale;
        const scaledWidth = Math.round(baseWidth * scaleMultiplier);
        const scaledFontSize = Math.round(baseFontSize * scaleMultiplier);
        const scaledMargin = Math.round(settings.margin * 2);

        // Create temporary container with TinyMCE-like styles
        const tempElement = document.createElement('div');
        tempElement.innerHTML = `
          <div class="preview-content" style="
            width: ${scaledWidth}px;
            min-height: ${baseHeight}px;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
            font-size: ${scaledFontSize}px;
            line-height: 1.4;
            color: #000;
            background: white;
            padding: ${scaledMargin}px;
            box-sizing: border-box;
            margin: 0;
            overflow: visible;
          ">
            <style>
              /* SCOPED STYLES - only apply to .preview-content */
              ${extractedStyles}

              /* Enhanced table styles to match TinyMCE */
              .preview-content table {
                border-collapse: collapse !important;
                width: 100% !important;
                margin: ${Math.round(8 * scaleMultiplier)}px 0 !important;
                border: ${Math.max(1, Math.round(1 * scaleMultiplier))}px solid #ddd !important;
                font-size: ${Math.round(11 * scaleMultiplier)}px !important;
                background: white !important;
              }

              .preview-content table th, .preview-content table td {
                border: ${Math.max(1, Math.round(1 * scaleMultiplier))}px solid #ddd !important;
                padding: ${Math.round(8 * scaleMultiplier)}px ${Math.round(6 * scaleMultiplier)}px !important;
                text-align: left !important;
                vertical-align: top !important;
                background: white !important;
              }

              .preview-content table th {
                background: #f5f5f5 !important;
                font-weight: bold !important;
              }

              /* Scale all elements proportionally - SCOPED to .preview-content */
              .preview-content h1 { font-size: ${Math.round(24 * scaleMultiplier)}px !important; margin: ${Math.round(16 * scaleMultiplier)}px 0 ${Math.round(8 * scaleMultiplier)}px 0 !important; }
              .preview-content h2 { font-size: ${Math.round(20 * scaleMultiplier)}px !important; margin: ${Math.round(14 * scaleMultiplier)}px 0 ${Math.round(6 * scaleMultiplier)}px 0 !important; }
              .preview-content h3 { font-size: ${Math.round(16 * scaleMultiplier)}px !important; margin: ${Math.round(12 * scaleMultiplier)}px 0 ${Math.round(4 * scaleMultiplier)}px 0 !important; }
              .preview-content p { margin: ${Math.round(8 * scaleMultiplier)}px 0 !important; font-size: ${scaledFontSize}px !important; }

              /* Page border like TinyMCE preview */
              .preview-content {
                background: white !important;
              }
            </style>
            ${pageContent}
          </div>
        `;

        // Position off-screen for rendering
        tempElement.style.position = 'absolute';
        tempElement.style.left = '-9999px';
        tempElement.style.top = '-9999px';
        tempElement.style.visibility = 'visible';
        document.body.appendChild(tempElement);

        // Wait for styles to apply
        await new Promise(resolve => setTimeout(resolve, 50));

        // Render with html2canvas
        const canvas = await html2canvas(tempElement.firstElementChild, {
          backgroundColor: '#ffffff',
          scale: 1,
          useCORS: true,
          letterRendering: true,
          width: scaledWidth,
          height: Math.max(baseHeight, tempElement.firstElementChild.scrollHeight),
          logging: false,
          windowWidth: scaledWidth,
          windowHeight: baseHeight,
          allowTaint: false,
          foreignObjectRendering: false
        });

        // Convert to data URL
        const bitmapDataUrl = canvas.toDataURL('image/png', 1.0);
        bitmaps.push({
          dataUrl: bitmapDataUrl,
          pageNumber: pageIndex + 1,
          width: canvas.width,
          height: canvas.height
        });

        // Cleanup
        document.body.removeChild(tempElement);

        console.log(`Generated bitmap for page ${pageIndex + 1}/${pagesToRender.length}`);
      }

      // Set both single (for backward compatibility) and multi-page bitmaps
      setPreviewBitmaps(bitmaps);
      if (bitmaps.length > 0) {
        setPreviewBitmap(bitmaps[0].dataUrl); // Keep first page as primary
      }

      console.log(`Successfully generated ${bitmaps.length} bitmap preview(s)`);

    } catch (error) {
      console.error('Bitmap preview generation failed:', error);
    } finally {
      setIsGeneratingBitmap(false);
    }
  };

  // Handle print settings change with bitmap preview update
  const handlePrintSettingsChange = async (e) => {
    const { name, value } = e.target;
    const newSettings = { ...printSettings, [name]: parseFloat(value) || value };
    setPrintSettings(newSettings);
    
    // Save to localStorage immediately
    localStorage.setItem('printSettings', JSON.stringify(newSettings));
    
    // Generate new bitmap preview with Chrome-like scaling
    await generateBitmapPreview(newSettings);
  };

  // Load saved print settings from localStorage
  useEffect(() => {
    const savedSettings = localStorage.getItem('printSettings');
    if (savedSettings) {
      try {
        const parsedSettings = JSON.parse(savedSettings);
        setPrintSettings(prev => ({ ...prev, ...parsedSettings }));
      } catch (error) {
        console.error('Error loading print settings:', error);
      }
    }
  }, []);

  // Generate bitmap preview when modal opens
  useEffect(() => {
    if (showPrintModal) {
      generateBitmapPreview();
    }
  }, [showPrintModal]);

  // Handle full-screen toggle
  const toggleFullScreen = () => {
    if (!isFullScreen) {
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
      } else if (containerRef.current.webkitRequestFullscreen) {
        containerRef.current.webkitRequestFullscreen();
      } else if (containerRef.current.msRequestFullscreen) {
        containerRef.current.msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
  };
console.log("ddddd===", originalDocument)
  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullScreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullScreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullScreenChange);
    document.addEventListener('msfullscreenchange', handleFullScreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullScreenChange);
      document.removeEventListener('msfullscreenchange', handleFullScreenChange);
    };
  }, []);

  // Handle split panel resizing
  const handleMouseDown = (e) => {
    if (viewMode === 'split') {
      setIsDragging(true);
      e.preventDefault();
      e.stopPropagation();
    }
  };

  useEffect(() => {
    if (!isDragging) return;

    let rafId = null;
    let lastX = null;

    const handleMouseMove = (e) => {
      e.preventDefault();
      e.stopPropagation();

      // Store the mouse position
      lastX = e.clientX;

      // Cancel any pending animation frame
      if (rafId) {
        cancelAnimationFrame(rafId);
      }

      // Use requestAnimationFrame for smooth updates
      rafId = requestAnimationFrame(() => {
        if (viewMode === 'split' && containerRef.current && lastX !== null) {
          const containerRect = containerRef.current.getBoundingClientRect();
          const newRatio = ((lastX - containerRect.left) / containerRect.width) * 100;
          const clampedRatio = Math.max(20, Math.min(80, newRatio));

          console.log('Dragging - X:', lastX, 'Container Left:', containerRect.left, 'Width:', containerRect.width, 'New Ratio:', clampedRatio);

          setSplitRatio(clampedRatio);
        }
      });
    };

    const handleMouseUp = (e) => {
      e.preventDefault();
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      setIsDragging(false);
    };

    // Add listeners to window instead of document for better event capture
    window.addEventListener('mousemove', handleMouseMove, { capture: true, passive: false });
    window.addEventListener('mouseup', handleMouseUp, { capture: true, passive: false });

    // Prevent text selection and iframe interaction during drag
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    document.body.style.pointerEvents = 'none'; // Prevent iframes from capturing events

    // Keep the resizer interactive
    if (resizerRef.current) {
      resizerRef.current.style.pointerEvents = 'auto';
    }

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      window.removeEventListener('mousemove', handleMouseMove, { capture: true });
      window.removeEventListener('mouseup', handleMouseUp, { capture: true });
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      document.body.style.pointerEvents = '';
      if (resizerRef.current) {
        resizerRef.current.style.pointerEvents = '';
      }
    };
  }, [isDragging, viewMode]);

  // Get TinyMCE preview styles - same styles used in TinyMCEEditor preview
  // const getTinyMCEPreviewStyles = () => {
  //   return `CSS styles commented out`;
  // };

  // Generate live preview HTML with current settings
  // const generateLivePreviewHTML = () => {
  //   // Get the content and original styles 
  //   const { content, styles } = generateExportHTMLWithSettings();
    
  //   // Get page dimensions based on settings
  //   const getPageDimensions = () => {
  //     const sizes = {
  //       a4: { width: printSettings.orientation === 'portrait' ? '210mm' : '297mm', height: printSettings.orientation === 'portrait' ? '297mm' : '210mm' },
  //       letter: { width: printSettings.orientation === 'portrait' ? '215.9mm' : '279.4mm', height: printSettings.orientation === 'portrait' ? '279.4mm' : '215.9mm' },
  //       a3: { width: printSettings.orientation === 'portrait' ? '297mm' : '420mm', height: printSettings.orientation === 'portrait' ? '420mm' : '297mm' },
  //       legal: { width: printSettings.orientation === 'portrait' ? '215.9mm' : '355.6mm', height: printSettings.orientation === 'portrait' ? '355.6mm' : '215.9mm' }
  //     };
  //     return sizes[printSettings.pageSize] || sizes.a4;
  //   };

  //   const pageDimensions = getPageDimensions();
    
  //   // Split content back into pages for preview
  //   const contentPages = [content]//.split('<div style="page-break-before: always;"></div>');

  //   let fullHTML = `
  //     <!DOCTYPE html>
  //     <html>
  //     <head>
  //       <meta charset="UTF-8">
  //       <title>${projectName} - PDF Preview</title>
  //       <style>
  //         /* Original TinyMCE styles */
  //         ${styles}
          
  //         @page {
  //           size: ${pageDimensions.width} ${pageDimensions.height};
  //           margin: ${printSettings.margin}mm;
  //         }
          
  //         body { 
  //           font-family: Arial, sans-serif !important;
  //           font-size: ${12 * printSettings.scale}px !important;
  //           line-height: 1.4 !important;
  //           color: #000 !important;
  //           margin: 0 !important;
  //           padding: ${printSettings.margin}mm !important;
  //           background: #f5f5f5 !important;
  //           width: 100%;
  //           min-height: 100vh;
  //           box-sizing: border-box;
  //         }
          
  //         .page-preview {
  //           width: 100%;
  //           max-width: ${pageDimensions.width};
  //           min-height: calc(${pageDimensions.height} - ${printSettings.margin * 2}mm);
  //           border: 1px solid #ccc;
  //           margin: 20px auto;
  //           padding: ${printSettings.margin}mm;
  //           page-break-after: always;
  //           background: white;
  //           box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  //           position: relative;
  //         }
          
  //         .page-preview:last-child {
  //           page-break-after: avoid;
  //           margin-bottom: 20px;
  //         }
          
  //         /* Enhanced table styles with original + preview styles */
  //         table {
  //           border-collapse: collapse !important;
  //           width: 100% !important;
  //           margin: 10px 0 !important;
  //           border: 1px solid #000 !important;
  //         }
          
  //         table th, table td {
  //           border: 1px solid #000 !important;
  //           padding: 8px !important;
  //           text-align: left !important;
  //           vertical-align: top !important;
  //           font-size: ${10 * printSettings.scale}px !important;
  //         }
          
  //         table th {
  //           background: #f0f0f0 !important;
  //           font-weight: bold !important;
  //         }
          
  //         h1, h2, h3, h4, h5, h6 {
  //           font-family: Arial, sans-serif !important;
  //           color: #000 !important;
  //           margin: 15px 0 10px 0 !important;
  //           font-weight: bold !important;
  //         }
          
  //         p {
  //           margin: 8px 0 !important;
  //           line-height: 1.5 !important;
  //         }
          
  //         .settings-info {
  //           position: fixed;
  //           top: 10px;
  //           right: 10px;
  //           background: rgba(0,0,0,0.8);
  //           color: white;
  //           padding: 10px;
  //           border-radius: 5px;
  //           font-size: 12px;
  //           z-index: 1000;
  //         }
          
  //         .page-number {
  //           text-align: center;
  //           font-size: 10px;
  //           color: #666;
  //           margin-bottom: 10px;
  //           font-weight: normal;
  //           border-bottom: 1px solid #ddd;
  //           padding-bottom: 5px;
  //         }
  //       </style>
  //     </head>
  //     <body>
  //       <div class="settings-info">
  //         ${printSettings.pageSize.toUpperCase()} • ${printSettings.orientation} • ${printSettings.margin}mm margins • ${printSettings.scale}x scale
  //       </div>
  //   `;

  //   // Add each page with preview styling
  //   contentPages.forEach((pageContent, index) => {
  //     if (pageContent.trim()) {  // Only add non-empty pages
  //       fullHTML += `
  //         <div class="page-preview">
  //           <div class="page-number">
  //             Page ${index + 1} of ${contentPages.length} - ${projectName}
  //           </div>
  //           ${pageContent || '<p>No content available for this page.</p>'}
  //         </div>
  //       `;
  //     }
  //   });

  //   fullHTML += '</body></html>';
  //   return fullHTML;
  // };

  // Generate preview HTML for PDF export (using current content)
  // const generatePreviewHTML = () => {
  //   // Capture latest content before generating preview
  //   const latestContent = captureCurrentContent();
  //   
  //   // Use current content if available, otherwise fall back to allHtmlPages
  //   const previewPages = allHtmlPages && allHtmlPages.length > 0 
  //     ? allHtmlPages.map((page, index) => {
  //         // Use current content for the active page
  //         return index === selectedPage - 1 ? latestContent : (page.content || page);
  //       })
  //     : [latestContent || '<p>No content available for PDF export</p>'];
  //
  //   // Use original styles for consistent preview
  //  // const allCustomStyles = getCombinedStyles();
  //
  //   let fullHTML = `
  //     <!DOCTYPE html>
  //     <html>
  //     <head>
  //       <meta charset="UTF-8">
  //       <title>${projectName} - Translation Export Preview</title>
  //       <style>
  //         ${getTinyMCEPreviewStyles()}
  //         
  //         .page-content {
  //           page-break-before: auto !important;
  //           page-break-after: always !important;
  //           min-height: 200mm !important;
  //           border-bottom: 2px dashed #ccc;
  //           margin-bottom: 20px;
  //           padding-bottom: 20px;
  //         }
  //         
  //         .page-content:last-child {
  //           page-break-after: avoid !important;
  //           border-bottom: none;
  //           margin-bottom: 0;
  //         }
  //         
  //         .page-number {
  //           text-align: center;
  //           font-size: 12px;
  //           color: #666;
  //           margin-bottom: 20px;
  //           font-weight: bold;
  //         }
  //         
  //       
  //       </style>
  //     </head>
  //     <body>
  //   `;
  //
  //   // Add each page with proper page breaks and page numbers
  //   previewPages.forEach((pageContent, index) => {
  //     fullHTML += `
  //       <div class="page-content">
  //         <div class="page-number">Page ${index + 1} of ${previewPages.length}</div>
  //         ${pageContent || '<p>No content available for this page.</p>'}
  //       </div>
  //     `;
  //   });
  //
  //   fullHTML += '</body></html>';
  //   return fullHTML;
  // };
const generateExportHTMLWithSettings = () => {
  const latestContent = captureCurrentContent();
  
  const exportPages = allHtmlPages && allHtmlPages.length > 0 
    ? allHtmlPages.map((page, index) => {
        return index === selectedPage - 1 ? latestContent : (page.content || page);
      })
    : [latestContent || '<p>No content available</p>'];

  // Extract original styles from the content
  let combinedOriginalStyles = '';
  exportPages.forEach((pageContent) => {
    const extractedStyles = extractStylesFromHTML(pageContent);
    if (extractedStyles) {
      combinedOriginalStyles += extractedStyles + '\n';
    }
  });

  let cleanHTML = '';
  exportPages.forEach((pageContent, index) => {
    if (index > 0) {
      cleanHTML += '<div style="page-break-before: always;"></div>';
    }
    // Remove style tags from content since we're extracting them separately
    const contentWithoutStyles = pageContent ? pageContent.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '') : '<p>No content available</p>';
    cleanHTML += contentWithoutStyles;
  });

  return { content: cleanHTML, styles: combinedOriginalStyles };
};

// generateExportHTML function commented out

  // Show PDF preview modal with content capture
  // const showPdfPreviewModal = () => {
  //   // Capture current content before showing preview
  //   captureCurrentContent();
  //   console.log(allHtmlPages);
  //   if (!allHtmlPages || allHtmlPages.length === 0) {
  //     if (!currentContent) {
  //       alert('No content available for PDF export');
  //       return;
  //     }
  //   }
  //   setShowPdfPreview(true);
  // };

  // Function to extract styles from HTML content (same as TinyMCE editor)
  const extractStylesFromHTML = (htmlContent) => {
    if (!htmlContent) return '';
    
    // Create a temporary DOM element to parse the HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    
    // Find all style tags
    const styleTags = tempDiv.querySelectorAll('style');
    let extractedStyles = '';
    
    styleTags.forEach(styleTag => {
      extractedStyles += styleTag.textContent + '\n';
    });
    
    return extractedStyles;
  };


  // Save and PDF Export function - saves translation first, then exports PDF
const exportToPDF = async () => {
  try {
    await onSave();
    setIsExporting(true);

    // Create PDF document
    const pdf = new (await import('jspdf')).jsPDF({
      orientation: printSettings.orientation,
      unit: 'mm',
      format: printSettings.pageSize,
      compress: false
    });

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    // Process each page
    for (let i = 0; i < previewBitmaps.length; i++) {
      const bitmap = previewBitmaps[i];

      // Create composite canvas for this page (with stamps for this specific page)
      const canvas = await createCompositeCanvas(bitmap, i);

      if (!canvas) {
        throw new Error(`Failed to create composite preview for page ${i + 1}`);
      }

      // Add new page if not the first one
      if (i > 0) {
        pdf.addPage();
      }

      // Convert canvas to image and add to PDF
      const dataUrl = canvas.toDataURL('image/png', 1.0);
      const imgProps = pdf.getImageProperties(dataUrl);

      // Calculate dimensions to fit page
      let imgWidth = pdfWidth;
      let imgHeight = (imgProps.height * pdfWidth) / imgProps.width;

      // If image is taller than page, scale to fit height
      if (imgHeight > pdfHeight) {
        imgHeight = pdfHeight;
        imgWidth = (imgProps.width * pdfHeight) / imgProps.height;
      }

      pdf.addImage(dataUrl, 'PNG', 0, 0, imgWidth, imgHeight);

      console.log(`Added page ${i + 1}/${previewBitmaps.length} to PDF`);
    }

    pdf.save(`${projectName.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_translation.pdf`);

    localStorage.setItem('printSettings', JSON.stringify(printSettings));
    console.log(`Successfully exported ${previewBitmaps.length} page(s) to PDF`);
  } catch (error) {
    console.error('PDF export failed:', error);
   // alert('Failed to export PDF. Please try again or ensure the preview is fully loaded.');
  } finally {
    setIsExporting(false);
  }
};

// Create a composite canvas with document content + positioned stamps
const createCompositeCanvas = async (bitmap, pageIndex) => {
  try {
    if (!bitmap || !bitmap.dataUrl) {
      throw new Error('No preview bitmap available');
    }

    // Create main canvas
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    // Load the base document image
    const baseImg = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = bitmap.dataUrl;
    });

    // Set canvas size to match base image
    canvas.width = baseImg.width;
    canvas.height = baseImg.height;

    // Draw base document
    ctx.drawImage(baseImg, 0, 0);

    // Draw stamps on top with CORS handling (only stamps for this page)
    const pageStamps = stamps.filter(stamp => stamp.pageIndex === pageIndex);
    if (pageStamps.length > 0) {
      for (const stamp of pageStamps) {
        try {
          // Convert external image to data URL to avoid CORS issues
          const stampDataUrl = await convertImageToDataUrl(stamp.src);

          const stampImg = await new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = stampDataUrl;
          });

          // Calculate stamp position and size relative to the base image
          const displayedImgs = previewContainerRef.current?.querySelectorAll('img');
          const displayedImg = displayedImgs?.[pageIndex];
          const scaleX = canvas.width / (displayedImg?.offsetWidth || canvas.width);
          const scaleY = canvas.height / (displayedImg?.offsetHeight || canvas.height);

          const scaledX = stamp.x * scaleX;
          const scaledY = stamp.y * scaleY;
          const scaledWidth = stamp.width * scaleX;
          const scaledHeight = stamp.height * scaleY;

          ctx.drawImage(stampImg, scaledX, scaledY, scaledWidth, scaledHeight);
        } catch (stampError) {
          console.warn('Failed to add stamp to PDF:', stampError);
        }
      }
    }

    return canvas;
  } catch (error) {
    console.error('Failed to create composite canvas:', error);
    return null;
  }
};

  // Calculate dynamic heights
  // const getViewerHeight = () => {
  //   if (isFullScreen) {
  //     return 'calc(100vh - 120px)'; // Account for toolbar
  //   }
  //   return 'calc(100vh - 200px)'; // Account for header and other UI
  // };

  // Extract and store original styles once
  useEffect(() => {
    if (htmlContent && !originalStyles) {
      const extractedStyles = extractStylesFromHTML(htmlContent);
      if (extractedStyles) {
        setOriginalStyles(extractedStyles);
        console.log('Original styles extracted and stored');
      }
    }
  }, [htmlContent, originalStyles]);

  // Capture current content from the active editor before view changes
  const captureCurrentContent = () => {
    if (editorRef.current) {
      const content = editorRef.current.getContent();
      if (content !== currentContent) {
        setCurrentContent(content);
        console.log('Captured content length:', content.length);
        
        // Update the parent component with the content
        if (onHtmlChange) {
          onHtmlChange(content);
        }
        
        // Update the current page in htmlFiles if in multi-page mode
        if (onHtmlUpdate) {
          onHtmlUpdate(content);
        }
        
        return content;
      }
    }
    return currentContent;
  };

  // Handle view mode changes with content synchronization
  const handleViewModeChange = (newMode) => {
    console.log(`🔄 Switching from ${viewMode} to ${newMode}`);
    console.log(`📝 htmlContent length before switch:`, htmlContent?.length || 0);
    console.log(`📝 currentContent length before switch:`, currentContent?.length || 0);
    console.log(`📝 editorRef exists:`, !!editorRef?.current);

    // Only capture content if we're switching FROM a view that has an editor
    // (split or editor-full), not from original-full which doesn't have an editor
    if (viewMode === 'split' || viewMode === 'editor-full') {
      const latestContent = captureCurrentContent();
      console.log(`📝 latestContent captured from ${viewMode}:`, latestContent?.length || 0);
    } else {
      console.log(`📝 Skipping content capture from ${viewMode} (no editor in this view)`);
    }

    // Update view mode
    viewModeRef.current = newMode;
    setViewMode(newMode);

    console.log(`✅ View mode changed to: ${newMode}, styles preserved: ${!!originalStyles}`);
  };

  // Get combined styles for consistent application across views
  // const getCombinedStyles = () => {
  //   const baseStyles = getTinyMCEPreviewStyles();
  //   const contentStyles = originalStyles;
  //   
  //   return `${baseStyles}\n${contentStyles}`;
  // };

  // Initialize current content when htmlContent prop changes
  useEffect(() => {
    if (htmlContent !== currentContent) {
      setCurrentContent(htmlContent);
    }
  }, [htmlContent, currentContent]);

  // Update edited title when documentTitle prop changes
  useEffect(() => {
    setEditedTitle(documentTitle);
    setCurrentTitle(documentTitle);
  }, [documentTitle]);

  // Update view mode ref when state changes
  useEffect(() => {
    viewModeRef.current = viewMode;
  }, [viewMode]);

  // Validate document name
  const validateDocumentName = (name) => {
    if (!name || name.trim().length === 0) {
      return 'Document name cannot be empty';
    }
    if (name.length > 255) {
      return 'Document name is too long (max 255 characters)';
    }
    // Check for invalid characters
    const invalidChars = /[<>:"/\\|?*]/;
    if (invalidChars.test(name)) {
      return 'Document name contains invalid characters';
    }
    return null;
  };

  // Handle document title rename API call
  const handleTitleSave = async () => {
    const trimmedTitle = editedTitle.trim();

    // Validate the title
    const error = validateDocumentName(trimmedTitle);
    if (error) {
      setTitleError(error);
      return;
    }

    if (trimmedTitle === currentTitle) {
      // No change, just exit edit mode
      setIsEditingTitle(false);
      setTitleError(null);
      return;
    }

    setIsSavingTitle(true);
    setTitleError(null);

    try {
      await apiClient.post(`/translations/${documentId}/rename`, {
        docName: trimmedTitle
      });

      // Success - update local state immediately for instant UI feedback
      setCurrentTitle(trimmedTitle);
      setIsEditingTitle(false);

      // Optional: Call a callback to notify parent of title change
      if (typeof onTitleChange === 'function') {
        onTitleChange(trimmedTitle);
      }

    } catch (error) {
      console.error('Error renaming document:', error);

      // Handle different error response formats
      let errorMessage = 'Failed to rename document';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setTitleError(errorMessage);
    } finally {
      setIsSavingTitle(false);
    }
  };

  // Handle title edit start
  const handleTitleEditStart = () => {
    setIsEditingTitle(true);
    setEditedTitle(currentTitle);
    setTitleError(null);
  };

  // Handle title edit cancel
  const handleTitleEditCancel = () => {
    setIsEditingTitle(false);
    setEditedTitle(currentTitle);
    setTitleError(null);
  };

  // Handle key events in title input
  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleTitleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleTitleEditCancel();
    }
  };

  // Convert Dropbox share URL to direct image URL
  const convertDropboxUrl = (shareUrl) => {
    if (!shareUrl) return null;
    // Convert Dropbox share URL to direct image URL
    return shareUrl.replace('?dl=0', '?raw=1').replace('dropbox.com', 'dl.dropboxusercontent.com');
  };

  // Convert external image to data URL to avoid CORS issues
  const convertImageToDataUrl = async (imageUrl) => {
    try {
      // Use a proxy canvas to convert external images to data URLs
      const tempCanvas = document.createElement('canvas');
      const tempCtx = tempCanvas.getContext('2d');

      const img = await new Promise((resolve, reject) => {
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.onload = () => resolve(image);
        image.onerror = () => {
          // If CORS fails, try without crossOrigin
          const fallbackImg = new Image();
          fallbackImg.onload = () => resolve(fallbackImg);
          fallbackImg.onerror = reject;
          fallbackImg.src = imageUrl;
        };
        image.src = imageUrl;
      });

      tempCanvas.width = img.width;
      tempCanvas.height = img.height;
      tempCtx.drawImage(img, 0, 0);

      return tempCanvas.toDataURL('image/png');
    } catch (error) {
      console.warn('Failed to convert image to data URL:', error);
      // Return original URL as fallback
      return imageUrl;
    }
  };

  // Find which page is currently most visible in the preview modal viewport
  const findMostVisiblePage = () => {
    const scrollContainer = document.getElementById('preview-pages-container');
    const pageElements = document.querySelectorAll('.page-preview-item');

    if (!scrollContainer || !pageElements.length) {
      return { pageIndex: 0, centerY: 100 };
    }

    const containerRect = scrollContainer.getBoundingClientRect();
    let mostVisiblePage = 0;
    let maxVisibleArea = 0;

    pageElements.forEach((pageEl, index) => {
      const pageRect = pageEl.getBoundingClientRect();

      // Calculate visible area
      const visibleTop = Math.max(pageRect.top, containerRect.top);
      const visibleBottom = Math.min(pageRect.bottom, containerRect.bottom);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);
      const visibleArea = visibleHeight * pageRect.width;

      if (visibleArea > maxVisibleArea) {
        maxVisibleArea = visibleArea;
        mostVisiblePage = index;
      }
    });

    // Calculate center Y position relative to the page (not viewport)
    const targetPage = pageElements[mostVisiblePage];
    const pageRect = targetPage.getBoundingClientRect();
    // const containerTop = scrollContainer.getBoundingClientRect().top;

    // Find the center of the visible portion of the page
    const visibleTop = Math.max(pageRect.top, containerRect.top);
    const visibleBottom = Math.min(pageRect.bottom, containerRect.bottom);
    const visibleCenter = (visibleTop + visibleBottom) / 2;

    // Convert to position relative to the page element itself
    const centerY = Math.max(50, visibleCenter - pageRect.top);

    return { pageIndex: mostVisiblePage, centerY };
  };

  // Add stamp from user profile
  const addStampFromProfile = () => {
    if (!userProfile?.stampImgUrl) return;

    const directUrl = convertDropboxUrl(userProfile.stampImgUrl);
    if (!directUrl) return;

    // Find which page is currently most visible in viewport
    const { pageIndex, centerY } = findMostVisiblePage();

    const newStamp = {
      id: Date.now() + Math.random(),
      src: directUrl,
      x: 100, // Center horizontally
      y: centerY, // Position in center of visible area
      width: 150,
      height: 100,
      name: 'User Stamp',
      type: 'stamp',
      pageIndex: pageIndex
    };
    setStamps(prev => [...prev, newStamp]);
  };

  // Add signature from user profile
  const addSignatureFromProfile = () => {
    if (!userProfile?.signatureImgUrl) return;

    const directUrl = convertDropboxUrl(userProfile.signatureImgUrl);
    if (!directUrl) return;

    // Find which page is currently most visible in viewport
    const { pageIndex, centerY } = findMostVisiblePage();

    const newStamp = {
      id: Date.now() + Math.random(),
      src: directUrl,
      x: 100, // Center horizontally
      y: centerY + 120, // Position below stamp area
      width: 200,
      height: 80,
      name: 'User Signature',
      type: 'signature',
      pageIndex: pageIndex
    };
    setStamps(prev => [...prev, newStamp]);
  };

  const removeStamp = (stampId) => {
    setStamps(prev => prev.filter(stamp => stamp.id !== stampId));
  };

  const updateStamp = (stampId, updates) => {
    setStamps(prev => prev.map(stamp =>
      stamp.id === stampId ? { ...stamp, ...updates } : stamp
    ));
  };

  const renderToolbar = () => (
    <div className="sticky top-0 z-[100] flex items-center justify-between bg-gray-800 text-white px-3 py-1.5 border-b border-gray-700 shadow-md">
      <div className="flex items-center gap-2">
        {/* View Mode Toggle */}
        <div className="flex items-center bg-gray-900 rounded-md p-0.5">
          <button
            className={`px-2 py-1 rounded text-xs font-medium transition-all ${
              viewMode === 'split'
                ? 'bg-blue-500 text-white shadow-sm'
                : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
            }`}
            onClick={() => handleViewModeChange('split')}
            title="Split view - View original and translation side by side"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 4H5a1 1 0 00-1 1v14a1 1 0 001 1h4m0-16v16m0-16h6m-6 16h6m0-16v16m0-16h4a1 1 0 011 1v14a1 1 0 01-1 1h-4" />
            </svg>
          </button>
          <button
            className={`px-2 py-1 rounded text-xs font-medium transition-all ${
              viewMode === 'original-full'
                ? 'bg-blue-500 text-white shadow-sm'
                : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
            }`}
            onClick={() => handleViewModeChange('original-full')}
            title="Original document full screen"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </button>
          <button
            className={`px-2 py-1 rounded text-xs font-medium transition-all ${
              viewMode === 'editor-full'
                ? 'bg-blue-500 text-white shadow-sm'
                : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
            }`}
            onClick={() => handleViewModeChange('editor-full')}
            title="Editor full screen"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
        </div>

        {/* Divider */}
        <div className="h-5 w-px bg-gray-700"></div>

        {/* Chatbot Toggle */}
        <div className="flex items-center gap-1">
          <button
            className={`px-2 py-1 rounded text-xs font-medium transition-all ${
              showChatbot
                ? 'bg-purple-500 text-white shadow-sm'
                : 'bg-gray-700 text-gray-400 hover:text-gray-200 hover:bg-gray-600'
            }`}
            onClick={() => setShowChatbot(!showChatbot)}
            title={showChatbot ? 'Hide AI assistant' : 'Show AI assistant'}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
          </button>
          {showChatbot && (
            <button
              className={`px-2 py-1 rounded text-xs font-medium transition-all ${
                chatbotMode === 'panel'
                  ? 'bg-purple-500 text-white shadow-sm'
                  : 'bg-gray-700 text-gray-400 hover:text-gray-200 hover:bg-gray-600'
              }`}
              onClick={() => setChatbotMode(chatbotMode === 'panel' ? 'overlay' : 'panel')}
              title={chatbotMode === 'panel' ? 'Switch to floating mode' : 'Switch to panel mode'}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {chatbotMode === 'panel' ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                )}
              </svg>
            </button>
          )}
        </div>

        {/* Page Navigation */}
        {totalPages > 1 && (
          <>
            <div className="h-5 w-px bg-gray-700"></div>
            <div className="flex items-center gap-1.5 bg-gray-900 rounded-md px-2 py-0.5">
              <button
                onClick={() => onPageChange && onPageChange(selectedPage - 1)}
                disabled={selectedPage <= 1}
                className="p-0.5 rounded text-gray-400 hover:text-white hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Previous page"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <span className="text-xs font-medium text-gray-300 px-1">
                {selectedPage}/{totalPages}
              </span>
              <button
                onClick={() => onPageChange && onPageChange(selectedPage + 1)}
                disabled={selectedPage >= totalPages}
                className="p-0.5 rounded text-gray-400 hover:text-white hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Next page"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </>
        )}
      </div>

      {/* Document Title */}
      <div className="flex-1 text-center px-4">
        {isEditingTitle ? (
          <div className="inline-flex flex-col items-center gap-0.5">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={editedTitle}
                onChange={(e) => setEditedTitle(e.target.value)}
                onKeyDown={handleTitleKeyDown}
                onBlur={handleTitleSave}
                className="bg-white text-gray-900 px-2 py-0.5 rounded text-sm font-semibold text-center min-w-0 max-w-96"
                style={{ width: Math.max(200, editedTitle.length * 8) + 'px' }}
                autoFocus
                disabled={isSavingTitle}
                placeholder="Document name"
              />
              {isSavingTitle && (
                <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              )}
            </div>
            {titleError && (
              <div className="text-red-300 text-[10px] bg-red-900 bg-opacity-50 px-1.5 py-0.5 rounded">
                {titleError}
              </div>
            )}
          </div>
        ) : (
          <h3
            className="text-sm font-semibold text-white cursor-pointer hover:bg-gray-700 hover:bg-opacity-50 px-2 py-0.5 rounded transition-colors group inline-flex items-center gap-1.5"
            onClick={handleTitleEditStart}
            title="Click to edit document name"
          >
            {currentTitle}
            <svg
              className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </h3>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1.5">
        {saving ? (
          <div className="flex items-center gap-1 text-gray-300 text-[10px] bg-gray-700 bg-opacity-50 px-1.5 py-0.5 rounded">
            <div className="w-2.5 h-2.5 border-2 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
            <span>Saving</span>
          </div>
        ) : lastSaved && (
          <div className="flex items-center gap-1 text-gray-400 text-[10px] bg-gray-700 bg-opacity-30 px-1.5 py-0.5 rounded">
            <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span>Last saved {formatRelativeTime(lastSaved)}</span>
          </div>
        )}
        <button
          className="px-2.5 py-1 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
          onClick={() => {
            if (editorRef.current) {
              // Step 1: Capture current editor content
              const currentPageContent = editorRef.current.getContent();

              // Step 2: Combine all pages (use current editor content for active page)
              const exportPages = allHtmlPages && allHtmlPages.length > 0
                ? allHtmlPages.map((page, index) => {
                    return index === selectedPage - 1 ? currentPageContent : (page.content || page);
                  })
                : [currentPageContent];

              // Step 3: Concatenate all pages with explicit page breaks
              const combinedContent = exportPages.map((pageContent, index) => {
                // Wrap each page in a div with page-break-after for all pages except the last
                if (index < exportPages.length - 1) {
                  return `<div style="page-break-after: always;">${pageContent}</div>`;
                }
                return pageContent;
              }).join('');

              // Step 4: Temporarily load all pages into editor
              editorRef.current.setContent(combinedContent);

              // Step 5: Trigger MCE print after content is loaded
              setTimeout(() => {
                editorRef.current.execCommand('mcePrint');

                // Step 6: Restore original single-page content after print dialog opens
                setTimeout(() => {
                  editorRef.current.setContent(currentPageContent);
                }, 100);
              }, 100);
            }
          }}
          disabled={saving || isExporting}
          title="Download and export document as PDF"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          {isExporting ? 'Exporting' : 'Download'}
        </button>
        <button
          className="p-1 bg-gray-700 text-gray-300 rounded hover:bg-gray-600 transition-colors"
          onClick={toggleFullScreen}
          title={isFullScreen ? 'Exit fullscreen' : 'Enter fullscreen'}
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {isFullScreen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
            )}
          </svg>
        </button>
      </div>
    </div>
  );

  // ...existing code...

const renderSplitView = () => (
  <div className={`flex flex-row h-full min-h-0`} style={{ height: '100%' }}>
    {/* Original Document Panel */}
    <div
      className="bg-white border-r border-gray-300 flex flex-col min-h-0"
      style={{
        width: chatbotMode === 'panel' ? `${splitRatio * 0.75}%` : `${splitRatio}%`
      }}
    >
      <div className="flex-1 min-h-0 overflow-auto">
        {originalDocument ? (
          <div 
            className="h-full w-full"
            ref={(el) => {
              if (el && documentViewerRef.current) {
                const persistentViewer = documentViewerRef.current;
                persistentViewer.style.position = 'static';
                persistentViewer.style.top = 'auto';
                persistentViewer.style.left = 'auto';
                persistentViewer.style.visibility = 'visible';
                if (persistentViewer.parentNode !== el) {
                  el.appendChild(persistentViewer);
                }
              }
            }}
          />
        ) : (
          <div className="flex items-center justify-center h-full bg-gray-50">
            <div className="text-center">
              <div className="text-4xl mb-2">📄</div>
              <p className="text-gray-500">Original document not available</p>
            </div>
          </div>
        )}
      </div>
    </div>

    {/* Resizer */}
    <div
      ref={resizerRef}
      className="relative w-1 bg-gray-300 hover:bg-blue-500 cursor-col-resize flex items-center justify-center transition-colors group"
      onMouseDown={handleMouseDown}
      style={{
        minWidth: '4px',
        // Expand hit area for easier grabbing
        paddingLeft: '4px',
        paddingRight: '4px',
        marginLeft: '-4px',
        marginRight: '-4px'
      }}
    >
      <div className="absolute inset-y-0 left-1/2 transform -translate-x-1/2 w-1 bg-gray-300 group-hover:bg-blue-500 transition-colors"></div>
      <div className="text-gray-600 text-xs select-none pointer-events-none relative z-10">⋮⋮</div>
    </div>

    {/* Editor Panel */}
    <div
      className="bg-white flex flex-col min-h-0"
      style={{
        width: chatbotMode === 'panel' ? `${(100 - splitRatio) * 0.75}%` : `${100 - splitRatio}%`
      }}
    >
      <div className="flex-1 min-h-0 overflow-auto">
        <TinyMCEEditor
          key={`editor-page-${selectedPage}`}
          editorRef={editorRef}
          initialValue={(() => {
            console.log(`🎨 Initializing TinyMCE in SPLIT view with htmlContent length:`, htmlContent?.length || 0);
            return htmlContent;
          })()}
          height="100%"
          onSelectionChange={onSelectionChange}
          customStyles={originalStyles}
          onAutoSave={async (content) => {
            // Validate content is not empty before saving
            const trimmedContent = content?.trim();
            if (!trimmedContent || trimmedContent === '' || trimmedContent === '<p></p>' || trimmedContent === '<br>') {
              console.warn('⚠️ Attempted to save empty content - blocking save');
              return;
            }

            console.log('✅ Auto-saving content, length:', content.length);
            // ONLY call onHtmlUpdate for auto-save (backend save + ref update)
            // DO NOT call onHtmlChange as it triggers state update and re-render
            if (onHtmlUpdate) {
              await onHtmlUpdate(content);
            }
          }}
          autoSaveDelay={5000}
        />
      </div>
    </div>

    {/* Chatbot Panel (when in panel mode) */}
    {showChatbot && chatbotMode === 'panel' && (
      <div className="w-80 bg-white border-l border-gray-300 flex flex-col min-h-0">
        <div className="flex-1 min-h-0 overflow-auto">
          <ChatbotWidget
            initialHtml={htmlContent}
            documentId={documentId}
            onHtmlUpdate={onHtmlUpdate}
            enabled={true}
            position="embedded"
            title={chatbotConfig.title || `${sourceLanguage.toUpperCase()} → ${targetLanguage.toUpperCase()} Assistant`}
            welcomeMessage={chatbotConfig.welcomeMessage || "Hi! I can help you improve this translation."}
            selectedText={selectedText}
            selectionContext={selectionContext}
            onProcessMessage={onProcessMessage}
            isEmbedded={true}
          />
        </div>
      </div>
    )}
  </div>
);

// ...existing code...

  const renderOriginalFullView = () => (
    <div className="bg-white flex flex-col h-full">
      <div className="flex-1 min-h-0 overflow-hidden">
        {originalDocument ? (
          <div 
            className="h-full w-full"
            ref={(el) => {
              if (el && documentViewerRef.current) {
                // Move the persistent viewer into this container
                const persistentViewer = documentViewerRef.current;
                persistentViewer.style.position = 'static';
                persistentViewer.style.top = 'auto';
                persistentViewer.style.left = 'auto';
                persistentViewer.style.visibility = 'visible';
                if (persistentViewer.parentNode !== el) {
                  el.appendChild(persistentViewer);
                }
              }
            }}
          />
        ) : (
          <div className="flex items-center justify-center h-full bg-gray-50">
            <div className="text-center">
              <div className="text-6xl mb-4">📄</div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">Original document not available</h3>
              <p className="text-gray-500 mb-4">Switch to editor view to continue translation work</p>
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                onClick={() => handleViewModeChange('editor-full')}
              >
                Open Editor
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderEditorFullView = () => (
    <div className="bg-white flex flex-col h-full">
      <div className="flex-1 min-h-0 overflow-hidden">
        <TinyMCEEditor
          key={`editor-page-${selectedPage}`}
          editorRef={editorRef}
          initialValue={(() => {
            console.log(`🎨 Initializing TinyMCE in EDITOR-FULL view with htmlContent length:`, htmlContent?.length || 0);
            return htmlContent;
          })()}
          height="100%"
          onSelectionChange={onSelectionChange}
          customStyles={originalStyles}
          onAutoSave={async (content) => {
            // Validate content is not empty before saving
            const trimmedContent = content?.trim();
            if (!trimmedContent || trimmedContent === '' || trimmedContent === '<p></p>' || trimmedContent === '<br>') {
              console.warn('⚠️ Attempted to save empty content - blocking save');
              return;
            }

            console.log('✅ Auto-saving content, length:', content.length);
            // ONLY call onHtmlUpdate for auto-save (backend save + ref update)
            // DO NOT call onHtmlChange as it triggers state update and re-render
            if (onHtmlUpdate) {
              await onHtmlUpdate(content);
            }
          }}
          autoSaveDelay={5000}
        />
      </div>
    </div>
  );

  return (
    <div
      ref={containerRef}
      className={`flex flex-col bg-gray-100 relative ${isFullScreen ? 'fixed inset-0 z-50 h-screen' : 'min-h-screen h-screen z-40'}`}
      style={isFullScreen ? { position: 'relative', zIndex: 2147483647 } : {}}
    >
      {renderToolbar()}

      {/* Persistent DocumentViewer - always rendered but hidden when not needed */}
      <div
        ref={documentViewerRef}
        className="absolute -top-[9999px] -left-[9999px] w-full h-full invisible"
      >
        {originalDocument && (
          <DocumentViewer
            documentUrl={originalDocument}
            title={documentTitle}
            documentType={documentType}
            showPageControls={false}
            flexibleHeight={true}
          />
        )}
      </div>

      <div className="relative flex-1 min-h-0">
        {viewMode === 'split' && renderSplitView()}
        {viewMode === 'original-full' && renderOriginalFullView()}
        {viewMode === 'editor-full' && renderEditorFullView()}
      </div>

      {/* Floating Chatbot (when in overlay mode) */}
      {showChatbot && chatbotMode === 'overlay' && (
        <div className="fixed bottom-4 right-4 z-40">
          <ChatbotWidget
            initialHtml={htmlContent}
            documentId={documentId}
            onHtmlUpdate={onHtmlUpdate}
            enabled={true}
            position="bottom-right"
            title={chatbotConfig.title || `${sourceLanguage.toUpperCase()} → ${targetLanguage.toUpperCase()} Assistant`}
            welcomeMessage={chatbotConfig.welcomeMessage || "Hi! I can help you improve this translation."}
            selectedText={selectedText}
            selectionContext={selectionContext}
            onProcessMessage={onProcessMessage}
          />
        </div>
      )}

      {/* Live PDF Preview Modal with Settings */}
      {showPrintModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center backdrop-blur-sm">
          <div className="bg-white rounded-xl w-[95vw] h-[90vh] max-w-7xl flex shadow-2xl">
            
            {/* Settings Panel */}
            <div className="w-80 bg-gray-50 rounded-l-xl border-r border-gray-200 flex flex-col">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">💾 Save & Export PDF</h3>
                <p className="text-sm text-gray-600">Adjust settings and see live preview</p>
              </div>
              
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Page Size:
                  </label>
                  <select 
                    name="pageSize" 
                    value={printSettings.pageSize} 
                    onChange={handlePrintSettingsChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="a4">A4 (210 × 297mm)</option>
                    <option value="letter">Letter (8.5 × 11 inch)</option>
                    <option value="a3">A3 (297 × 420mm)</option>
                    <option value="legal">Legal (8.5 × 14 inch)</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Orientation:
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => handlePrintSettingsChange({ target: { name: 'orientation', value: 'portrait' } })}
                      className={`p-3 border rounded-lg text-sm font-medium transition-colors ${
                        printSettings.orientation === 'portrait'
                          ? 'bg-blue-100 border-blue-500 text-blue-700'
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      📄 Portrait
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePrintSettingsChange({ target: { name: 'orientation', value: 'landscape' } })}
                      className={`p-3 border rounded-lg text-sm font-medium transition-colors ${
                        printSettings.orientation === 'landscape'
                          ? 'bg-blue-100 border-blue-500 text-blue-700'
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      📄 Landscape
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Margin: {printSettings.margin}mm
                  </label>
                  <input 
                    type="range"
                    name="margin" 
                    value={printSettings.margin} 
                    onChange={handlePrintSettingsChange}
                    min="5"
                    max="50"
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>5mm</span>
                    <span>50mm</span>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Scale: {printSettings.scale}x
                  </label>
                  <input 
                    type="range"
                    step="0.1" 
                    min="0.5" 
                    max="2" 
                    name="scale" 
                    value={printSettings.scale} 
                    onChange={handlePrintSettingsChange}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>0.5x</span>
                    <span>1x</span>
                    <span>2x</span>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-blue-800 mb-2">Current Settings:</h4>
                  <div className="text-sm text-blue-700 space-y-1">
                    <div>📏 {printSettings.pageSize.toUpperCase()}</div>
                    <div>🔄 {printSettings.orientation}</div>
                    <div>📐 {printSettings.margin}mm margins</div>
                    <div>🔍 {printSettings.scale}x scale</div>
                  </div>
                </div>
                
                {/* Signature/Stamp Section */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Signatures & Stamps:
                  </label>
                  <div className="space-y-3">
                    {/* User's Stamp Button */}
                    {userProfile?.stampImgUrl && (
                      <button
                        onClick={addStampFromProfile}
                        className="w-full p-4 border-2 border-blue-300 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors text-center"
                      >
                        <div className="text-blue-700">
                          <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <p className="text-sm font-medium">Add My Stamp</p>
                          <p className="text-xs text-blue-600">Click to add your official stamp</p>
                        </div>
                      </button>
                    )}

                    {/* User's Signature Button */}
                    {userProfile?.signatureImgUrl && (
                      <button
                        onClick={addSignatureFromProfile}
                        className="w-full p-4 border-2 border-green-300 rounded-lg bg-green-50 hover:bg-green-100 transition-colors text-center"
                      >
                        <div className="text-green-700">
                          <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                          <p className="text-sm font-medium">Add My Signature</p>
                          <p className="text-xs text-green-600">Click to add your signature</p>
                        </div>
                      </button>
                    )}

                    {/* Show message if no stamp/signature available */}
                    {!userProfile?.stampImgUrl && !userProfile?.signatureImgUrl && (
                      <div className="p-4 border-2 border-dashed border-gray-300 rounded-lg text-center">
                        <div className="text-gray-500">
                          <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <p className="text-sm font-medium">No Stamp or Signature Available</p>
                          <p className="text-xs text-gray-400">Upload them in your profile settings</p>
                        </div>
                      </div>
                    )}

                    {/* Active stamps list */}
                    {stamps.length > 0 && (
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        <p className="text-xs text-gray-600 font-medium">Added to document:</p>
                        {stamps.map(stamp => (
                          <div key={stamp.id} className="bg-gray-50 p-2 rounded space-y-1">
                            <div className="flex items-center justify-between">
                              <div className="flex-1 truncate">
                                <span className="text-xs text-gray-700">{stamp.name}</span>
                              </div>
                              <button
                                onClick={() => removeStamp(stamp.id)}
                                className="text-red-500 hover:text-red-700 text-sm ml-2"
                              >
                                ×
                              </button>
                            </div>
                            {previewBitmaps.length > 1 && (
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-gray-500">Page:</span>
                                {previewBitmaps.map((_, idx) => (
                                  <button
                                    key={idx}
                                    onClick={() => updateStamp(stamp.id, { pageIndex: idx })}
                                    className={`text-xs px-2 py-0.5 rounded ${
                                      stamp.pageIndex === idx
                                        ? 'bg-blue-500 text-white'
                                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                                    }`}
                                  >
                                    {idx + 1}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-green-800 mb-2">✨ High Quality Export:</h4>
                  <div className="text-sm text-green-700 space-y-1">
                    <div>🖨️ PNG format for crisp text</div>
                    <div>📊 3x scale + 192 DPI rendering</div>
                    <div>⚡ Optimized text rendering</div>
                    <div>🖋️ Drag & drop signatures/stamps</div>
                  </div>
                </div>
              </div>
              
              <div className="p-6 border-t border-gray-200 space-y-3">
                <button
                  className="w-full px-4 py-3 bg-green-300 rounded-lg hover:bg-green-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                  onClick={exportToPDF}
                  disabled={saving || isExporting}
                >
                  {saving ? '💾 Saving...' : isExporting ? '📄 Exporting...' : '💾 Save & Export PDF'}
                </button>
                <button
                  className="w-full px-4 py-3 text-gray-600 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                  onClick={() => setShowPrintModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>

            {/* Preview Panel */}
            <div className="flex-1 flex flex-col bg-white rounded-r-xl">
              <div className="p-6 border-b border-gray-200 bg-gray-50 rounded-tr-xl">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-800">📄 Live Preview</h3>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <span>{(allHtmlPages && allHtmlPages.length > 0 ? allHtmlPages.length : 1)} page{(allHtmlPages && allHtmlPages.length !== 1) ? 's' : ''}</span>
                    <span>•</span>
                    <span>{projectName}</span>
                  </div>
                </div>
              </div>
              
              <div className="flex-1 overflow-hidden bg-gray-100 p-4">
                <div
                  ref={previewContainerRef}
                  className="w-full h-full border border-gray-300 rounded-lg bg-white shadow-sm flex items-center justify-center relative"
                >
                  {isGeneratingBitmap ? (
                    <div className="text-center p-8">
                      <div className="w-16 h-16 border-4 border-blue-100 border-l-blue-600 rounded-full animate-spin mb-4 mx-auto"></div>
                      <div className="text-lg font-medium text-blue-600 mb-2">Generating Chrome-like Preview</div>
                      <div className="text-sm text-gray-600">Rasterizing content to bitmap image...</div>
                      <div className="text-xs text-gray-500 mt-2">🎯 Using same method as Chrome print dialog</div>
                    </div>
                  ) : previewBitmaps.length > 0 ? (
                    <div className="w-full h-full overflow-auto p-4" id="preview-pages-container">
                      <div className="space-y-6 relative" ref={previewContainerRef}>
                        {previewBitmaps.map((bitmap, pageIndex) => (
                          <div key={pageIndex} className="relative inline-block w-full page-preview-item" data-page-index={pageIndex}>
                            <div className="text-center mb-2 text-sm font-medium text-gray-700">
                              Page {bitmap.pageNumber} of {previewBitmaps.length}
                            </div>
                            <div className="relative inline-block mx-auto">
                              <img
                                src={bitmap.dataUrl}
                                alt={`Page ${bitmap.pageNumber} Preview`}
                                className="max-w-full h-auto border rounded shadow-lg"
                                data-page-index={pageIndex}
                                style={{
                                  imageRendering: '-webkit-optimize-contrast'
                                }}
                              />
                            </div>
                          </div>
                        ))}

                        {/* Draggable Stamp Overlays - Positioned absolutely over all pages */}
                        {stamps.map(stamp => {
                          // Calculate absolute position based on page
                          const pageElements = previewContainerRef.current?.querySelectorAll('.page-preview-item');
                          const targetPage = pageElements?.[stamp.pageIndex];
                          // const containerTop = previewContainerRef.current?.offsetTop || 0;

                          let absoluteY = stamp.y;
                          if (targetPage) {
                            absoluteY = targetPage.offsetTop + stamp.y;
                          }

                          return (
                            <Rnd
                              key={stamp.id}
                              size={{ width: stamp.width, height: stamp.height }}
                              position={{ x: stamp.x, y: absoluteY }}
                              onDrag={(e, d) => {
                                // Detect which page the stamp is over during drag
                                const pageElements = previewContainerRef.current?.querySelectorAll('.page-preview-item');
                                if (pageElements) {
                                  for (let i = 0; i < pageElements.length; i++) {
                                    const pageEl = pageElements[i];
                                    const rect = pageEl.getBoundingClientRect();
                                    const stampCenterY = d.y + stamp.height / 2;

                                    if (stampCenterY >= rect.top && stampCenterY <= rect.bottom) {
                                      if (stamp.pageIndex !== i) {
                                        // Update page index if stamp moved to different page
                                        const relativeY = d.y - pageEl.offsetTop;
                                        updateStamp(stamp.id, { pageIndex: i, y: relativeY });
                                      }
                                      break;
                                    }
                                  }
                                }
                              }}
                              onDragStop={(e, d) => {
                                // Final position update
                                const pageElements = previewContainerRef.current?.querySelectorAll('.page-preview-item');
                                if (pageElements) {
                                  const targetPage = pageElements[stamp.pageIndex];
                                  if (targetPage) {
                                    const relativeY = d.y - targetPage.offsetTop;
                                    updateStamp(stamp.id, { x: d.x, y: relativeY });
                                  }
                                }
                              }}
                              onResizeStop={(e, direction, ref, delta, position) => {
                                updateStamp(stamp.id, {
                                  width: ref.offsetWidth,
                                  height: ref.offsetHeight,
                                });
                              }}
                              className="border-2 border-dashed border-blue-400 hover:border-blue-600"
                              style={{ position: 'absolute', zIndex: 10 }}
                              enableResizing={{
                                top: true,
                                right: true,
                                bottom: true,
                                left: true,
                                topRight: true,
                                bottomRight: true,
                                bottomLeft: true,
                                topLeft: true,
                              }}
                            >
                              <div className="w-full h-full relative group">
                                <img
                                  src={stamp.src}
                                  alt={stamp.name}
                                  className="w-full h-full object-contain"
                                  draggable={false}
                                />
                                <button
                                  onClick={() => removeStamp(stamp.id)}
                                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  ×
                                </button>
                              </div>
                            </Rnd>
                          );
                        })}
                      </div>
                      <div className="text-center mt-4 text-sm text-gray-600">
                        🎯 {previewBitmaps.length} Page(s) • Scale: {printSettings.scale}x • {stamps.length > 0 ? `${stamps.length} stamp(s)/signature(s)` : 'Drag stamps across pages freely'}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center p-8">
                      <div className="text-6xl mb-4">🖨️</div>
                      <div className="text-lg font-medium text-gray-700 mb-2">Preview Loading...</div>
                      <div className="text-sm text-gray-500">
                        Content will be rasterized to bitmap and scaled
                      </div>
                    </div>
                  )}

                </div>
              </div>
            </div>
            
          </div>
        </div>
      )}

      {/* PDF Preview Modal */}
      {/* {showPdfPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center backdrop-blur-sm" onClick={() => setShowPdfPreview(false)}>
          <div className="bg-white rounded-xl w-[90vw] h-[85vh]  max-h-[800px] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50 rounded-t-xl">
              <h3 className="text-lg font-semibold text-gray-800">📄 PDF Export Preview</h3>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>{allHtmlPages.length} page{allHtmlPages.length !== 1 ? 's' : ''}</span>
                <span>•</span>
                <span>{projectName}</span>
              </div>
              <button 
                className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded transition-colors"
                onClick={() => setShowPdfPreview(false)}
                title="Close preview"
              >
                ×
              </button>
            </div>
            
            <div className="flex-1 overflow-hidden">
              <iframe
                ref={previewIframeRef}
                srcDoc={generatePreviewHTML()}
                className="w-full h-full border-none bg-white"
                title="PDF Preview"
              />
            </div>
            
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <button
                className="px-5 py-2 text-gray-600 bg-gray-200 rounded hover:bg-gray-300 transition-colors"
                onClick={() => setShowPdfPreview(false)}
              >
                Cancel
              </button>
              <button
                className="px-5 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                onClick={exportToPDF}
                disabled={isExporting}
              >
                {isExporting ? '📄 Exporting...' : '📄 Export PDF'}
              </button>
            </div>
          </div>
        </div>
      )} */}
    </div>
  );
};

export default FullScreenTranslationViewer;